/**
 * lod_visibility — view-cone weighting and plan-based occlusion for the
 * per-building LOD refinement (settlement LOD0 research L4, 2026-09-03).
 * Pure; the settlement module feeds it the camera and each site's building
 * boxes.
 *
 * Distance and screen size alone made the refinement load LOD0 for every
 * building in the band, including the ones behind the player and the ones
 * hidden behind a closer block. Two cheap inputs fix most of that without a
 * GPU readback: a foveated cone weight on the priority (CesiumJS defers
 * tiles outside a cone about the view direction) and a line-of-sight march
 * against the settlement's own placement boxes (a city plan is a set of
 * boxes on pads — a building whose upper half is behind a taller, closer box
 * along the ray is occluded). Occluded candidates are not promoted; a LOD0
 * building occluded for a while is demoted. The HZB "drawn last frame" bit
 * remains the sharper signal when the instancer gains a readback.
 */
import type { SpatialHash2D, Placed } from './settle_hotspots.js';

/** A placed building as an occluder: footprint circle (conservative, inscribed) + its top. */
export interface Occluder extends Placed { top: number; id: number }

/** Cesium's foveated cone: this fraction of the half field of view is "centre" (weight 1). */
export const VIEW_CONE_FRACTION = 0.3;
/** Priority weight at the edge of the field of view… */
export const VIEW_EDGE_WEIGHT = 0.35;
/** …and behind the camera. */
export const VIEW_BEHIND_WEIGHT = 0.1;
/** Line-of-sight sample spacing (m) and the height margin a blocker must clear. */
export const OCCLUSION_STEP_M = 12;
export const OCCLUSION_MARGIN_M = 2;
/** A LOD0 building occluded this long is demoted to LOD1. */
export const OCCLUDED_DEMOTE_MS = 3000;
/** Occlusion is only evaluated inside this distance (the LOD0 band); farther buildings are cheap anyway. */
export const OCCLUSION_MAX_DIST_M = 900;

/**
 * Priority weight from the horizontal angle between the view direction and
 * the direction to a building: 1 inside the foveated cone, falling to
 * VIEW_EDGE_WEIGHT at the field-of-view edge and VIEW_BEHIND_WEIGHT behind.
 */
export function viewConeWeight(dx: number, dz: number, fx: number, fz: number, fovRad: number, cone = VIEW_CONE_FRACTION): number {
  const dl = Math.hypot(dx, dz), fl = Math.hypot(fx, fz);
  if (dl < 1e-6 || fl < 1e-6) return 1;
  const cos = (dx * fx + dz * fz) / (dl * fl);
  const ang = Math.acos(Math.max(-1, Math.min(1, cos)));
  const half = Math.max(0.05, fovRad / 2);
  const inner = half * cone;
  if (ang <= inner) return 1;
  if (ang <= half) return 1 - (1 - VIEW_EDGE_WEIGHT) * ((ang - inner) / Math.max(1e-6, half - inner));
  if (ang <= Math.PI / 2) return VIEW_EDGE_WEIGHT - (VIEW_EDGE_WEIGHT - VIEW_BEHIND_WEIGHT) * ((ang - half) / Math.max(1e-6, Math.PI / 2 - half));
  return VIEW_BEHIND_WEIGHT;
}

/**
 * True when the line of sight from the eye to the target's roofline is
 * blocked by a placed box closer than the target: march the XZ segment in
 * OCCLUSION_STEP_M steps and look for an occluder whose footprint contains
 * the sample and whose top clears the ray's height there by the margin.
 * The target itself (by id) and blockers hugging the target are ignored.
 */
export function occludedByPlan(
  eye: { x: number; y: number; z: number },
  target: { id: number; x: number; z: number; groundY: number; h: number },
  occluders: SpatialHash2D<Occluder>,
  step = OCCLUSION_STEP_M, margin = OCCLUSION_MARGIN_M,
): boolean {
  const dx = target.x - eye.x, dz = target.z - eye.z;
  const dist = Math.hypot(dx, dz);
  if (dist < step * 1.5) return false;
  const aimY = target.groundY + target.h; // aim at the roofline: a building whose top clears the blocker is visible (a tower behind a block), a short one whose top does not is hidden
  const n = Math.floor(dist / step);
  for (let i = 1; i < n; i++) {
    const t = (i * step) / dist;
    const sx = eye.x + dx * t, sz = eye.z + dz * t;
    const rayY = eye.y + (aimY - eye.y) * t;
    const remaining = dist - i * step;
    if (remaining < step) break; // hugging the target: its own neighbours, not blockers
    const hit = occluders.anyWithin(sx, sz, 0, (o) => o.id !== target.id && o.top > rayY + margin && Math.hypot(o.x - sx, o.z - sz) <= o.r);
    if (hit) return true;
  }
  return false;
}

/** Inscribed footprint radius of a box (conservative: a blocker must really be in the way). */
export function footprintRadius(width: number, depth: number): number {
  return Math.max(1, Math.min(width, depth) / 2);
}
